<template>
  <v-container fluid>
    <h5 class="text-h5">Auditoria de Stock</h5>
    <v-divider></v-divider>
    <v-row dense>
      <v-col cols="12">
        <v-row>
          <v-col cols="12" md="3">
            <operation-combobox
                v-model="filters[0].value"
                @change="performSearch"
            >
            </operation-combobox>
          </v-col>

          <v-col cols="12" md="2">
            <date-input
                label="Fecha desde"
                v-model="filters[1].value"
                @input="fetch"
                clearable
            ></date-input>
          </v-col>

          <v-col cols="12" md="2">
            <date-input
                label="Fecha hasta"
                v-model="filters[2].value"
                @input="fetch"
                clearable
            ></date-input>
          </v-col>

        </v-row>
      </v-col>

      <v-col cols="12">

        <v-data-table
            class="mt-3"
            :headers="headers"
            :items="items"
            :search="search"
            :server-items-length="totalItems"
            :loading="loading"
            :page.sync="pageNumber"
            :items-per-page.sync="itemsPerPage"
            :sort-by.sync="orderBy"
            :sort-desc.sync="orderDesc"
            :footer-props="{ itemsPerPageOptions: [5, 10, 25, 50] }"
            @update:page="fetch"
            @update:sort-by="fetch"
            @update:sort-desc="fetch"
            @update:items-per-page="fetch"
            show-expand :single-expand="true"

        >


          <template v-slot:item.items="{ item }">
            <v-chip-group>
              <v-chip v-for="(i) in getItemsWithProducts(item.items)" :key="i.product.id">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-avatar v-bind="attrs"
                              v-on="on">
                      <img :src="i.product.image"/>
                    </v-avatar>
                  </template>
                  <span>{{ i.product.name }}</span>
                </v-tooltip>
                {{ i.finalQuantity }}
              </v-chip>
            </v-chip-group>
          </template>

          <template v-slot:item.createdAt="{ item }">
            {{ getDateTimeFormat(item.createdAt) }}
          </template>

          <template v-slot:item.branch="{ item }">
            {{ item.branch ? item.branch.name : '' }}
          </template>


          <template v-slot:item.branchDestination="{ item }">
            {{ item.branchDestination ? item.branchDestination.name : '' }}
          </template>


          <template v-slot:item.user="{ item }">
            {{ item.user ? item.user.username : '' }}
          </template>

          <template v-slot:item.total="{ item }">
            <b>{{ item.items.reduce((a, i) => (a + i.changeQuantity), 0) }}</b>
          </template>

          <template v-slot:item.operation="{ item }">
            {{ $t('delivery.stockOperation.' + item.operation) }}
          </template>

          <template v-slot:expanded-item="{headers, item}">
            <td :colspan="headers.length">
              <v-card class="px-2 my-2">
                <v-card-title>{{$t('delivery.stockOperation.'+item.operation)}}</v-card-title>
                <v-card-subtitle v-if="item.reason">{{item.reason}}</v-card-subtitle>
              <v-simple-table>
                <thead>
                  <tr>
                    <th>Imagen</th>
                    <th>Producto</th>
                    <th>Cantidad Inicial</th>
                    <th>{{getTransaction(item.operation)}}</th>
                    <th>Cantidad Final</th>
                  </tr>
                </thead>

                <tbody>
                  <tr v-for="(i) in getItemsWithProducts(item.items)" :key="i.product.id">
                    <td>  <v-avatar> <v-img   eager :src="i.product.image"/>  </v-avatar> </td>
                    <td> {{ i.product.name }} </td>
                    <td> {{ i.initialQuantity }}</td>
                    <td> {{ i.changeQuantity }}</td>
                    <td> {{ i.finalQuantity }}</td>
                  </tr>
                </tbody>
              </v-simple-table>
              </v-card>
            </td>
          </template>


          <template slot="no-data">
            <div class="text-xs-center" v-t="'common.noData'"></div>
          </template>

          <template slot="loading">
            <div class="text-xs-center" v-t="'common.loading'"></div>
          </template>


        </v-data-table>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import StockTransactionProvider from "../../../providers/StockTransactionProvider";

import {DayjsMixin, DateInput} from "@dracul/dayjs-frontend"
import OperationCombobox from "@/modules/delivery/combobox/StockOperationCombobox/StockOperationCombobox";


export default {
  name: "StockTransactionAuditPage",
  components: {OperationCombobox,DateInput},
  mixins: [DayjsMixin],
  data() {
    return {
      items: [],
      totalItems: null,
      loading: false,
      orderBy: 'createdAt',
      orderDesc: true,
      itemsPerPage: 5,
      pageNumber: 1,
      search: '',
      filters: [
        {
          field: 'operation',
          operator: 'eq', //(eq|contain|regex|gt|lt|lte|gte)
          value: ""
        },
        {
          field: 'createdAt',
          operator: 'gte', //(eq|contain|regex|gt|lt|lte|gte)
          value: null
        },
        {
          field: 'createdAt',
          operator: 'lt', //(eq|contain|regex|gt|lt|lte|gte)
          value: null
        }
      ]
    }
  },
  computed: {
    getTransaction(){
      return operation => {
        switch (operation){
          case 'ADD':
            return 'Agregados'
          case 'REMOVE':
            return 'Removidos'
          case 'MOVE':
            return 'Movidos'
          case 'SOLD':
            return 'Vendidos'
        }
      }
    },
    getItemsWithProducts(){
      return items => {
        return items.filter(i => i.product && i.product.id)
      }
    },
    headers() {
      return [
        //Entity Headers
        {text: this.$t('delivery.stockTransaction.labels.user'), value: 'user'},
        {text: this.$t('delivery.stockTransaction.labels.createdAt'), value: 'createdAt'},
        {text: this.$t('delivery.stockTransaction.labels.operation'), value: 'operation'},
        {text: this.$t('delivery.stockTransaction.labels.reason'), value: 'reason'},
        {text: this.$t('delivery.stockTransaction.labels.branch'), value: 'branch'},
        // {text: this.$t('delivery.stockTransaction.labels.reason'), value: 'reason'},
        {text: this.$t('delivery.stockTransaction.labels.branchDestination'), value: 'branchDestination'},
        {text: this.$t('delivery.stockTransaction.labels.total'), value: 'total'},
        // {text: this.$t('delivery.stockTransaction.labels.items'), value: 'items'},
      ]
    },
    getOrderBy() {
      return (Array.isArray(this.orderBy)) ? this.orderBy[0] : this.orderBy
    },
    getOrderDesc() {
      return (Array.isArray(this.orderDesc)) ? this.orderDesc[0] : this.orderDesc
    }
  },
  created() {
    this.fetch()
  },
  methods: {
    performSearch() {
      this.pageNumber = 1
      this.fetch()
    },
    fetch() {
      this.loading = true
      StockTransactionProvider.paginateStockTransaction(
          this.pageNumber,
          this.itemsPerPage,
          this.search,
          this.filters,
          this.getOrderBy,
          this.getOrderDesc
      ).then(r => {
        this.items = r.data.paginateStockTransaction.items
        this.totalItems = r.data.paginateStockTransaction.totalItems
      }).catch(err => {
        console.error(err)
      }).finally(() => this.loading = false)
    }
  }

}
</script>


<style>

.v-data-table__expanded__row{
  background-color: #00e676 !important;
}
</style>
